const NBSP = "\u00a0";

// <br/>·\r\n·\r·\n (연속은 1회) → \n 하나
const LINE_BREAKS = /(?:<br\s*\/?>|\r\n|\r|\n)+/gi;
// 앞뒤의 공백뿐인 줄 (값 앞뒤에 붙어 오는 \n 이 빈 줄로 보이지 않게)
const LEADING_BLANK_LINES = /^(?:[ \u00a0]*\n)+/;
const TRAILING_BLANK_LINES = /(?:\n[ \u00a0]*)+$/;
// 셀 맨 앞·맨 끝의 공백만 대상이다. 셀 안 줄바꿈 경계는 DART 뷰어처럼 그대로 둔다 — 백엔드가
// 줄바꿈 뒤 공백은 이미 &nbsp; 로 적어 들여쓰기가 보이고, 줄바꿈 앞 끝 공백까지 보이게 하면
// DART 와 달라진다.
const EDGE_SPACES = /^[ \u00a0]+|[ \u00a0]+$/g;
const LEADING_NBSP = /^\u00a0*/;
// 숫자·부호·괄호·대시·%만 있거나 빈 칸은 값 자리다(「-」 포함).
const VALUE_LIKE = /^[\s\u00a0\d,.()\-+%△▲▽▼]*$/;

/**
 * DSD 셀 원문 텍스트. 화면에 그리는 값과 복사하는 값을 이 객체가 만든다.
 *
 * 원본 DSD 는 들여쓰기를 셀 앞 공백으로 적는다. 백엔드는 이를 `' ' + \xa0…`(옛 형식) 또는
 * `\xa0\xa0…`(새 형식)로 내려주는데, 브라우저는 셀 맨 앞·맨 끝의 일반 공백을 지우고
 * 화면마다 `trim()` 도 섞여 있어 들여쓰기가 화면마다 다르게(또는 아예 안) 보였다.
 * 표시값은 앞·끝 공백을 칸 수 그대로 \xa0 로 바꿔 white-space 설정과 무관하게 보이게 한다.
 */
export class DsdCellText {
  private constructor(private readonly raw: string) {}

  static of(raw: string | null | undefined): DsdCellText {
    return new DsdCellText(raw ?? "");
  }

  /** 화면 표시값 — 줄바꿈은 \n, 앞뒤 빈 줄 제거, 앞·끝 공백은 칸 수만큼 \xa0 */
  get display(): string {
    const text = this.lines()
      .replace(LEADING_BLANK_LINES, "")
      .replace(TRAILING_BLANK_LINES, "");
    if (text.trim() === "") return "";
    return text.replace(EDGE_SPACES, (spaces) => NBSP.repeat(spaces.length));
  }

  /** 표시값 앞의 들여쓰기(\xa0 칸들) — 단어 단위 diff 처럼 원문 공백을 잃은 렌더 앞에 붙인다 */
  get indent(): string {
    return this.display.match(LEADING_NBSP)?.[0] ?? "";
  }

  /** 값 자리인가 — 숫자·부호·괄호·대시만 있거나 비어 있다(「-」 포함). 머리·이름 칸이 아니다 */
  get isValueLike(): boolean {
    return VALUE_LIKE.test(this.raw);
  }

  /** 숫자 값인가 — 값 자리이면서 숫자가 하나라도 있다(「-」·빈 칸은 아니다) */
  get isNumericValue(): boolean {
    return /\d/.test(this.raw) && this.isValueLike;
  }

  /** 복사값 — 엑셀에서 숫자로 읽히도록 앞뒤 공백·빈 줄을 뺀다. 셀 안 줄바꿈은 \n */
  get clipboard(): string {
    return this.lines().trim();
  }

  private lines(): string {
    return this.raw.replace(LINE_BREAKS, "\n");
  }
}
